import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { validateResetPasswordUrl,changePassword, ErrorType } from '../api';

const ChangePassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({ password: '', confirmPassword: '' });
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [token, setToken] = useState('');
  const [isValidating, setIsValidating] = useState(true);

  // Read app name from environment variable, default to 'Court Booking System' if not set
  const appName = process.env.REACT_APP_APP_NAME || 'Court Booking System';

  // Get token from URL and validate it on component mount
  useEffect(() => {
    const validateToken = async () => {
      const params = new URLSearchParams(location.search);
      const urlToken = params.get('token');
      const urlUuid = params.get('uuid');

      if (!urlToken || !urlUuid) {
        setError('Invalid or missing reset token');
        setIsValidating(false);
        return;
      }

      try {
        // Make GET request to validate token and get JWT
        const response = await validateResetPasswordUrl({ token: urlToken, uuid: urlUuid });
        
        // Store the JWT token for later use      
        setToken(response.token);
        setIsValidating(false);
      } catch (err) {
        switch (err.type) {
          case ErrorType.AUTHENTICATION:
            // Handle authentication errors, such as invalid token
            setError('Invalid or expired reset link. Please request a new password reset.');
            break;
          case ErrorType.VALIDATION:
            setError(err.message);
            break;
          case ErrorType.NETWORK:
            setError('Unable to connect to the server. Please check your internet connection and try again.');
            break;
          case ErrorType.SERVER:
            setError('The server encountered an error. Please try again later.');
            break;
          default:
            setError('An unexpected error occurred. Please try again.');
        }
      } finally {
        setIsValidating(false);
      }
    };

    validateToken();
  }, [location]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError(''); // Clear error when user starts typing
  };

  const validateForm = () => {
    if (!form.password || !form.confirmPassword) {
      setError('All fields are required');
      return false;
    }
    if (form.password !== form.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    if (form.password.length < 6) {
      setError('Password must be at least 6 characters long');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {

      console.log("CALLING CHANGEPASSWORD WITH TOKEN", token);
      const response = await changePassword({
        password: form.password,
        token
      });
      setSuccessMessage(response.message);
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login', { state: { message: response.message } });
      }, 3000);

    } catch (err) {
      switch (err.type) {
        case ErrorType.AUTHENTICATION:
          // Handle authentication errors, such as invalid token
          setError('Invalid or expired reset link. Please request a new password reset.');
          break;
        case ErrorType.VALIDATION:
          setError(err.message);
          break;
        case ErrorType.NETWORK:
          setError('Unable to connect to the server. Please check your internet connection and try again.');
          break;
        case ErrorType.SERVER:
          setError('The server encountered an error. Please try again later.');
          break;
        default:
          setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (isValidating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-sm">
          <div className="relative flex items-center mb-6">
            <h2 className="w-full text-center text-2xl font-bold text-slate-800">{appName}</h2>
          </div>
          <div className="flex items-center justify-center">
            <svg className="animate-spin h-8 w-8 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span className="ml-3 text-slate-600">Validating reset link...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-100">
      <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-sm">
        <div className="relative flex items-center mb-6">
          <h2 className="w-full text-center text-2xl font-bold text-slate-800">{appName}</h2>
        </div>
        <p className="text-slate-600 text-center mb-6">Please enter your new password</p>
        
        {successMessage && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-md text-sm">
            {successMessage}
          </div>
        )}

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-md text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-slate-600 mb-1">
              New Password
            </label>
            <input
              id="password"
              type="password"
              name="password"
              autoComplete="new-password"
              className="w-full px-4 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 outline-none transition"
              placeholder="••••••••"
              value={form.password}
              onChange={handleChange}
              disabled={isLoading}
            />
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-slate-600 mb-1">
              Confirm New Password
            </label>
            <input
              id="confirmPassword"
              type="password"
              name="confirmPassword"
              autoComplete="new-password"
              className="w-full px-4 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 outline-none transition"
              placeholder="••••••••"
              value={form.confirmPassword}
              onChange={handleChange}
              disabled={isLoading}
            />
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className={`w-full py-2 px-4 rounded-md text-white font-medium transition-all duration-200 ${
              isLoading 
                ? 'bg-blue-400 cursor-not-allowed' 
                : 'bg-blue-600 hover:bg-blue-700 active:bg-blue-800'
            } flex items-center justify-center`}
          >
            {isLoading ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Updating...
              </>
            ) : (
              'Update Password'
            )}
          </button>
        </form>

        <p className="mt-6 text-center text-sm text-slate-600">
          Remember your password?{' '}
          <Link 
            to="/login" 
            className="font-medium text-blue-600 hover:text-blue-500 transition"
          >
            Sign in instead
          </Link>
        </p>
      </div>
    </div>
  );
};

export default ChangePassword; 